import { useState, useEffect, useRef } from "react";
import styles from "./complaints.module.css";
import { Typography, Dialog, DialogTitle, DialogContent, Snackbar, Slide, IconButton, TablePagination } from "@mui/material";
import { Home, X, ArrowLeft } from "react-feather";
import Helmet from "react-helmet";
import Footer from "../../components/Footer";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import ComplaintFilter from "./components/ComplaintFilter";
import ComplaintCard from "./components/ComplaintCard";
import ComplaintMobileCard from "./components/ComplaintMobileCard";
import AddComplaint from "./components/AddComplaint";
import axios from "axios";
import { TableLoader } from "../../components/LoaderC";
import { AddLogCallAPI } from "../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";

const withCategoryImage = (item) => {
  if (item?.category === 'Noise')
    return { ...item, image: '/static/img/noise.svg' };
  if (item?.category === 'Garbage Removal')
    return { ...item, image: '/static/img/garbage.svg' };
  return { ...item, image: '/static/img/other.svg' };
}

const Complaints = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", status: "", priority: "" });
  const [sortData, setSortData] = useState("");
  const [allData, setAllData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);
  const latestRequest = useRef(0);

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchKey(filterData.searchKey), filterData.searchKey ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filterData.searchKey]);

  useEffect(() => {
    const requestId = ++latestRequest.current;

    const getRequests = async () => {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        const response = await axios.get(process.env.REACT_APP_CASE_API_URL + "v1/complaints/paginated", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: page + 1,
            per_page: perPage,
            search: searchKey || undefined,
            property_id: (filterData.property !== "" && filterData.property !== "all") ? filterData.property : undefined,
            apt_id: (filterData.apartment !== "" && filterData.apartment !== "all") ? filterData.apartment : undefined,
            status: filterData.status || undefined,
            priority: filterData.priority || undefined,
            sort: sortData === 'erliest' ? 'earliest' : 'latest',
          },
        });

        // Ignore responses from requests that were superseded by newer filters/pages
        if (requestId !== latestRequest.current) return;

        setAllData((response.data?.data || []).map(withCategoryImage));
        setTotalCount(response.data?.pagination?.total || 0);
        setLoadError(false);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        console.error('[Complaints] Failed to load complaint list', error);
        setLoadError(true);
        toast.error(error?.response?.data?.message || 'Unable to load complaints. Please try again.');
      } finally {
        if (requestId === latestRequest.current)
          setContentLoading(false);
      }
    }
    getRequests();
  }, [getAccessTokenSilently, page, perPage, searchKey, filterData.property, filterData.apartment, filterData.status, filterData.priority, sortData, reloadKey]);

  const changeCStatus = async (item, cVal) => {
    if (item?.current_status !== cVal) {
      setLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.put(process.env.REACT_APP_CASE_API_URL + 'v1/complaints/' + item?.id, { 'current_status': cVal }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then(response => {
        setLoading(false);
        setSnackbarMsg(response.data.message);
        setSnackbarOpen(true);

        setAllData(prev => {
          return prev?.map(i => {
            if (i?.id === item?.id)
              return { ...i, current_status: cVal };
            return i;
          })
        })

        // The item may no longer match the active status filter, so refresh the page
        if (filterData.status !== "" && filterData.status !== "all")
          setReloadKey(prev => prev + 1);

        let logData = {
          'title': 'Complaint status has been updated',
          'description': [
            'Name: ' + response.data.data.title,
            'Complaints number: ' + response.data.data.complaint_no,
            'Apartment: ' + response.data.data.property_name + ' - ' + response.data.data.apt_name,
            'Tenant: ' + response.data.data.tenant_name
          ]
        }
        AddLogCallAPI(logData, token);
      }).catch(error => {
        setLoading(false);
        if (typeof error.response !== 'undefined')
          toast.error(error.response.data.message);
      });
    }
  }

  const onSubmitLocal = () => {
    // Reload from the server so the new complaint lands on the correct page and the total stays accurate
    setReloadKey(prev => prev + 1);
  }

  const renderPagination = () => (<TablePagination
    count={totalCount}
    color="primary"
    page={page}
    rowsPerPage={perPage}
    onPageChange={(e, v) => setPage(v)}
    rowsPerPageOptions={[
      { label: '4', value: 4 },
      { label: '8', value: 8 },
      { label: '16', value: 16 },
      { label: '50', value: 50 },
    ]}
    onRowsPerPageChange={(e) => {
      setPage(0);
      setPerPage(e.target.value);
    }}
    labelRowsPerPage={'Complaints per Page:'}
  />);

  return (<>
    {loading && <Loader />}
    <Helmet title="Complaints" />
    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Complaints</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Cases</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Complaints</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>

          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <ComplaintFilter setAddModalOpen={setAddModalOpen.bind(this)} filterData={filterData} setFilterData={setFilterData.bind(this)} sortData={sortData} setSortData={setSortData.bind(this)} setPage={setPage.bind(this)} />

              {allData.length > 0 && <>
                <div className={`${styles.NotesRow}`}>
                  {allData?.map((item) => {
                    return <ComplaintCard key={item?.id} item={item} changeCStatus={changeCStatus.bind(this)} />;
                  })}

                  <div className={`${styles.NotesRow} Pagination TablePaginationNew List`}>
                    {renderPagination()}
                  </div>
                </div>

                <div className={`${styles.ListMobileView}`}>
                  {allData?.map((item) => {
                    return <ComplaintMobileCard key={item?.id} item={item} changeCStatus={changeCStatus.bind(this)} />
                  })}

                  <div className={`${styles.MobilePagination} Pagination TablePaginationNew List`}>
                    {renderPagination()}
                  </div>
                </div>
              </>}

              {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                <p className={`${styles.NoDataText}`}>Something went wrong</p>
                <p className={`${styles.NoDataTextSub}`}>We couldn't load complaints. Please refresh the page to try again.</p>
              </div>)}

              {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                <div className={`${styles.NoDataIMG}`}>
                  <img src="/static/img/no-request.png" alt="No Data" />
                </div>
                <p className={`${styles.NoDataText}`}>All caught up!</p>
                <p className={`${styles.NoDataTextSub}`}>There are currently no complaints to show.</p>
              </div>)}

            </div>
          </div>)}


        </div>
      </div>
    </div>

    <Dialog
      open={addModalOpen}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setAddModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setAddModalOpen(false)}><ArrowLeft /></button>
        New Complaint
        <button onClick={(e) => setAddModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <AddComplaint setAddModalOpen={setAddModalOpen.bind(this)} tenant_id={0} setLoading={setLoading.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} onSubmitLocal={onSubmitLocal.bind(this)} complaintData={null} />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(Complaints, {
  onRedirecting: () => <Loader />,
});